import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useId } from '../../hooks/useId.js';
import type {
  AccordionContentProps,
  AccordionItemProps,
  AccordionMode,
  AccordionRootProps,
  AccordionTriggerProps,
} from './types.js';

interface AccordionRootContextValue {
  mode: AccordionMode;
  collapsible: boolean;
  openSet: ReadonlySet<string>;
  toggle: (value: string) => void;
  baseId: string;
  registerTrigger: (value: string, el: HTMLButtonElement | null) => void;
  focusAdjacent: (from: string, direction: 1 | -1 | 'first' | 'last') => void;
}

interface AccordionItemContextValue {
  value: string;
  disabled: boolean;
  triggerId: string;
  contentId: string;
  open: boolean;
}

const AccordionContext = createContext<AccordionRootContextValue | null>(null);
const AccordionItemContext = createContext<AccordionItemContextValue | null>(null);

function useAccordion(): AccordionRootContextValue {
  const ctx = useContext(AccordionContext);
  if (ctx === null) {
    throw new Error('Accordion.Item must be used inside <Accordion>');
  }
  return ctx;
}

function useAccordionItem(): AccordionItemContextValue {
  const ctx = useContext(AccordionItemContext);
  if (ctx === null) {
    throw new Error('Accordion.Trigger / Content must be used inside <Accordion.Item>');
  }
  return ctx;
}

function toSet(
  value: string | ReadonlyArray<string> | undefined,
  mode: AccordionMode,
): Set<string> {
  if (value === undefined) return new Set();
  if (typeof value === 'string') return value === '' ? new Set() : new Set([value]);
  return new Set(mode === 'single' ? value.slice(0, 1) : value);
}

function fromSet(set: Set<string>, mode: AccordionMode): string | ReadonlyArray<string> {
  if (mode === 'single') {
    if (set.size === 0) return '';
    const [first] = set;
    return first ?? '';
  }
  return Array.from(set);
}

/** WAI-ARIA Accordion pattern.
 *  https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 *
 *  - Each item has a header button (`<Trigger>`) with `aria-expanded`
 *    and `aria-controls`, and a region (`<Content>`) with `role=region`
 *    and `aria-labelledby`.
 *  - Keyboard: ↑↓ moves focus between triggers; Home/End jump to the
 *    first/last. Enter/Space on a trigger toggles.
 *  - Single-mode mirrors a radio group but isn't one (the a11y pattern
 *    permits a plain disclosure semantic even when only one item is
 *    open at a time, because the a11y label still reads "expanded"). */
function AccordionRoot(props: AccordionRootProps): ReactNode {
  const {
    mode = 'single',
    value,
    defaultValue,
    onValueChange,
    collapsible = true,
    className,
    children,
  } = props;

  const [currentValue, setValue] = useControllableState<string | ReadonlyArray<string>>({
    defaultValue: defaultValue ?? (mode === 'single' ? '' : []),
    ...(value !== undefined ? { value } : {}),
    ...(onValueChange !== undefined ? { onChange: onValueChange } : {}),
  });

  const openSet = useMemo(() => toSet(currentValue, mode), [currentValue, mode]);

  const triggersRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  const toggle = useCallback(
    (v: string) => {
      const next = new Set(openSet);
      if (next.has(v)) {
        if (!collapsible && next.size === 1) return;
        next.delete(v);
      } else {
        if (mode === 'single') next.clear();
        next.add(v);
      }
      setValue(fromSet(next, mode));
    },
    [openSet, mode, collapsible, setValue],
  );

  const registerTrigger = useCallback((v: string, el: HTMLButtonElement | null) => {
    if (el === null) triggersRef.current.delete(v);
    else triggersRef.current.set(v, el);
  }, []);

  const focusAdjacent = useCallback((from: string, direction: 1 | -1 | 'first' | 'last') => {
    /* Use DOM order (Map preserves insertion order in JS) for nav —
     * matches what a screen reader / tab-order user would expect. */
    const entries = Array.from(triggersRef.current.entries()).filter(([, el]) => !el.disabled);
    if (entries.length === 0) return;
    const idx = entries.findIndex(([k]) => k === from);
    let next = 0;
    if (direction === 'first') next = 0;
    else if (direction === 'last') next = entries.length - 1;
    else if (idx === -1) next = 0;
    else next = (idx + direction + entries.length) % entries.length;
    const target = entries[next];
    if (target !== undefined) target[1].focus();
  }, []);

  const baseId = useId('acc');
  const ctx = useMemo<AccordionRootContextValue>(
    () => ({ mode, collapsible, openSet, toggle, baseId, registerTrigger, focusAdjacent }),
    [mode, collapsible, openSet, toggle, baseId, registerTrigger, focusAdjacent],
  );

  return (
    <AccordionContext.Provider value={ctx}>
      <div
        className={['vq-accordion', className].filter(Boolean).join(' ')}
        data-component="Accordion"
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

function AccordionItem(props: AccordionItemProps): ReactNode {
  const root = useAccordion();
  const open = root.openSet.has(props.value);
  const itemCtx = useMemo<AccordionItemContextValue>(
    () => ({
      value: props.value,
      disabled: props.disabled ?? false,
      triggerId: `${root.baseId}-trigger-${props.value}`,
      contentId: `${root.baseId}-content-${props.value}`,
      open,
    }),
    [props.value, props.disabled, root.baseId, open],
  );

  return (
    <AccordionItemContext.Provider value={itemCtx}>
      <div
        className={['vq-accordion__item', props.className].filter(Boolean).join(' ')}
        data-state={open ? 'open' : 'closed'}
      >
        {props.children}
      </div>
    </AccordionItemContext.Provider>
  );
}

function AccordionTrigger(props: AccordionTriggerProps): ReactNode {
  const root = useAccordion();
  const item = useAccordionItem();

  const onKey = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        root.focusAdjacent(item.value, 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        root.focusAdjacent(item.value, -1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        root.focusAdjacent(item.value, 'first');
      } else if (e.key === 'End') {
        e.preventDefault();
        root.focusAdjacent(item.value, 'last');
      }
    },
    [item.value, root],
  );

  return (
    <h3 style={{ margin: 0, font: 'inherit', fontWeight: 'inherit' }}>
      <button
        type="button"
        id={item.triggerId}
        aria-expanded={item.open}
        aria-controls={item.contentId}
        disabled={item.disabled}
        className={['vq-accordion__trigger', props.className].filter(Boolean).join(' ')}
        ref={(el) => {
          root.registerTrigger(item.value, el);
        }}
        onClick={() => {
          if (!item.disabled) root.toggle(item.value);
        }}
        onKeyDown={onKey}
      >
        <span className="vq-accordion__label">{props.children}</span>
        <svg
          className="vq-accordion__indicator"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="4 6 8 10 12 6" />
        </svg>
      </button>
    </h3>
  );
}

function AccordionContent(props: AccordionContentProps): ReactNode {
  const item = useAccordionItem();
  const keepMounted = props.keepMounted ?? true;

  if (!item.open && !keepMounted) return null;

  return (
    <div className="vq-accordion__content-wrap" data-open={item.open ? 'true' : 'false'}>
      <div
        role="region"
        id={item.contentId}
        aria-labelledby={item.triggerId}
        hidden={!item.open}
        className={['vq-accordion__content', props.className].filter(Boolean).join(' ')}
      >
        <div className="vq-accordion__content-inner">{props.children}</div>
      </div>
    </div>
  );
}

interface AccordionCompound {
  (props: AccordionRootProps): ReactNode;
  Item: typeof AccordionItem;
  Trigger: typeof AccordionTrigger;
  Content: typeof AccordionContent;
}

export const Accordion: AccordionCompound = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
});
